import { useEffect, useRef, useState } from 'react';
import { track } from '../lib/analytics.js';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getProperty, propertyPhotos } from '../lib/property-config.js';
import { reviewSitesPhrase } from '../lib/review-summary.js';
import { vacationRentalJsonLd, DIRECT_DISCOUNT_PCT } from '../lib/property-schema.js';
import { fetchQuote } from '../lib/booking-api.js';
import { CONTACT_EMAIL } from '../lib/site-config.js';
import { buildFaqItems, savingsAnswer } from '../lib/faq.js';
import AvailabilityCalendar from '../components/booking/AvailabilityCalendar.jsx';
import Gallery from '../components/booking/Gallery.jsx';
import QuoteSummary from '../components/booking/QuoteSummary.jsx';
import BookingForm from '../components/booking/BookingForm.jsx';
import BriefSnapshot from '../components/booking/BriefSnapshot.jsx';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import NotFound from '../components/NotFound.jsx';
import Section, { Eyebrow } from '../components/Section.jsx';
import Seo from '../components/Seo.jsx';
import { canonicalOrigin } from '../lib/brand.js';

// FAQPage schema alongside the VacationRental one: AI answer engines lift
// marked-up Q&A pairs that answer plainly. Text lives in faq.js (shared with
// the build-time prerender); the visible <Faq> below mirrors it with JSX.
function faqPageJsonLd(property) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: buildFaqItems(property, CONTACT_EMAIL).map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a }
    }))
  };
}

// The objection-handling stack below the booking module. Every fact here is
// sourced from property-config.js / welcome-guides.js — the slots that are
// still null (host photo, rating, review quotes, cancellation tiers, nightly
// rate) render an honest fallback instead of an invented number.

function HostBlock({ property }) {
  return (
    <Section eyebrow="Who you're booking with" title={`Your hosts, ${property.hosts}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        {property.hostPhoto && (
          <img
            src={property.hostPhoto}
            width="112"
            height="112"
            alt={property.hosts}
            className="h-28 w-28 shrink-0 rounded-full border border-navy-100 object-cover"
          />
        )}
        <div>
          <p className="prose-navy">{property.hostIntro}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="rounded-lg bg-navy-900 px-4 py-2 font-medium text-cream-50 no-underline hover:bg-navy-800"
            >
              Email us directly
            </a>
            <span className="text-navy-500">{CONTACT_EMAIL} · we reply within a few hours</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Reviews({ property }) {
  const { rating, count, quotes } = property.reviews || {};
  // The rating stands on its own: it is the strongest trust signal on the page
  // and it should not wait on quote text being copied across. Quotes layer in
  // underneath whenever they land.
  const hasRating = Boolean(rating && count);
  const sites = reviewSitesPhrase(property.reviews);
  return (
    <Section eyebrow="Guests" title="Reviews">
      {hasRating ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-display text-3xl font-semibold leading-none text-navy-900">
              {rating}
              <span className="text-lg text-cream-600"> / 5</span>
            </p>
            <p className="text-sm text-navy-600">
              across {count} reviews{sites ? ` ${sites}` : ''}
            </p>
          </div>
          <p className="mt-3 leading-relaxed">
            Same home, same hosts, same calendar.{' '}
            <a
              href={property.airbnbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-navy-900 underline"
            >
              Read the reviews on our Airbnb listing
            </a>
            , then book it here without the service fee.
          </p>
          {quotes?.length > 0 && (
            <div className="mt-5 space-y-4">
              {quotes.map((q, i) => (
                <blockquote key={i} className="border-l-2 border-cream-500 pl-4">
                  <p className="font-serif leading-relaxed text-navy-800">“{q.text}”</p>
                  <footer className="mt-1.5 text-xs text-navy-500">
                    {q.guestFirstName}
                    {q.date ? ` · ${q.date}` : ''} · via Airbnb
                  </footer>
                </blockquote>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="leading-relaxed">
          This is the same home we host on Airbnb: same calendar, same hosts.{' '}
          <a
            href={property.airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-navy-900 underline"
          >
            Read our guest reviews on the Airbnb listing
          </a>
          , then book it here without the service fee.
        </p>
      )}
    </Section>
  );
}

function HomeDetails({ property }) {
  return (
    <Section eyebrow="The home" title="Beds, baths & amenities">
      <p className="text-sm text-navy-600">
        {property.bedrooms} bedrooms · {property.bathrooms} baths · sleeps {property.sleeps}
      </p>
      <dl className="mt-4 space-y-2 text-sm">
        {property.beds.map((b) => (
          <div key={b.room} className="sm:flex sm:gap-4">
            <dt className="w-44 shrink-0 font-medium text-navy-900">{b.room}</dt>
            <dd className="text-navy-700">{b.bed}</dd>
          </div>
        ))}
      </dl>
      <ul className="mt-5 grid gap-x-6 gap-y-1.5 text-sm leading-relaxed text-navy-700 sm:grid-cols-2">
        {property.amenities.map((a, i) => (
          <li key={i} className="flex gap-2">
            <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-cream-500" aria-hidden="true" />
            <span>{a}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Neighborhood({ property }) {
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(property.mapQuery)}&t=m&z=15&output=embed`;
  return (
    <Section eyebrow="Where you are" title="The neighborhood">
      <p className="prose-navy">{property.neighborhood}</p>
      <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-navy-700">
        {property.distances.map((d) => (
          <li key={d.place}>
            <span className="font-medium text-navy-900">{d.place}</span> · {d.distance}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-navy-700">
        Weighing Mount Pleasant against downtown, or picking a beach?{' '}
        <Link to="/guides" className="text-navy-900 underline">
          Our Charleston guides
        </Link>{' '}
        walk through those calls.
      </p>
      <div className="mt-4 overflow-hidden rounded-xl border border-navy-100">
        <div className="aspect-[16/9] w-full">
          <iframe
            src={mapSrc}
            title={`Map of ${property.name}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="h-full w-full"
          />
        </div>
      </div>
    </Section>
  );
}

function CancellationPolicy({ property }) {
  return (
    <Section eyebrow="Before you commit" title="Cancellation policy">
      {property.cancellationTiers?.length ? (
        <dl className="space-y-2 rounded-xl border border-navy-100 bg-cream-100/60 px-5 py-4 text-sm">
          {property.cancellationTiers.map((t) => (
            <div key={t.window} className="sm:flex sm:gap-4">
              <dt className="w-56 shrink-0 font-medium text-navy-900">{t.window}</dt>
              <dd className="text-navy-700">{t.refund}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="rounded-xl border border-navy-100 bg-cream-100/60 px-5 py-4 text-sm leading-relaxed text-navy-700">
          Your exact refund terms are itemized with your quote and shown again on the
          checkout page before you pay. Nothing is charged on this page. Want them in
          writing first?{' '}
          {/* -my-2 py-2: tap padding the negative margins hand back, so the
              link grows and the line it sits in does not. */}
          <a href={`mailto:${CONTACT_EMAIL}?subject=Cancellation policy`} className="-my-2 inline-block py-2 text-navy-900 underline">
            Email us
          </a>{' '}
          and we'll send the policy for your dates.
        </div>
      )}
    </Section>
  );
}

// Wording mirrored in src/lib/faq.js (FAQPage JSON-LD + prerender) — keep
// the two in sync when an answer changes.
function Faq({ property }) {
  const items = [
    {
      q: 'What happens after I click Book direct?',
      a: `A quick step asks who's coming and how to reach you. Then you're taken to our booking system's secure payment page showing the same itemized total as your quote; nothing is charged until you confirm there. Once payment completes, your dates are locked on our calendar (the one Airbnb and Vrbo sync from) and you get a confirmation right away. Check-in details like directions, lockbox, and Wi-Fi arrive before arrival.`
    },
    {
      q: 'Is this really the same home as on Airbnb?',
      a: (
        <>
          Yes. Same house, same calendar, same hosts. You can compare it against{' '}
          <a href={property.airbnbUrl} target="_blank" rel="noopener noreferrer" className="text-navy-900 underline">
            our Airbnb listing
          </a>{' '}
          side by side. Booking direct just removes the platform between us.
        </>
      )
    },
    {
      q: 'How much does booking direct save?',
      // Shared with the JSON-LD and the prerender so the three cannot drift.
      a: savingsAnswer(property)
    },
    ...(property.fromNightly && property.peakNightly
      ? [
          {
            q: 'What do nightly rates run?',
            a: `From about $${property.fromNightly} a night in the slow season to $${property.peakNightly} and up in peak weeks. Whatever the season, booking direct takes ${DIRECT_DISCOUNT_PCT}% off nightly rates automatically. Pick dates on the calendar for your exact total.`
          }
        ]
      : []),
    {
      q: 'Are pets allowed?',
      a: `No. The cottage is pet-free, no exceptions, which keeps it reliably comfortable for guests with allergies. Traveling with a service animal? Email ${CONTACT_EMAIL} before booking and we'll sort out the details.`
    },
    {
      q: 'Who do I talk to if something comes up?',
      a: `Us: ${property.hosts}. You'll have our cell from booking to checkout, and ${CONTACT_EMAIL} reaches us anytime. No support queue, no platform inbox.`
    }
  ];
  return (
    <Section eyebrow="Good to know" title="Questions, answered">
      <dl className="space-y-5">
        {items.map((item) => (
          <div key={item.q}>
            {/* text-lg: the answer is 17px serif, and the question must not read smaller. */}
            <dt className="font-display text-lg font-semibold text-navy-900">{item.q}</dt>
            <dd className="prose-navy mt-1 text-navy-700">{item.a}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

// Deep-link handoff: ?checkin=&checkout=&adults=&children= pre-selects the
// stay, so a link handed to a guest inside an AI conversation lands on a
// priced quote instead of an empty calendar. The same template is published in
// the page's ReserveAction (property-schema.js), in /api/stay/<slug>, and in
// llms.txt, so anything that reads any of those can produce a working link.
// Nothing here trusts the URL: a malformed, past, or too-short range is
// dropped and the guest just gets the normal empty calendar.
const YMD = /^\d{4}-\d{2}-\d{2}$/;

function todayYmd() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function nightsBetween(start, end) {
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  return Math.round((Date.UTC(ye, me - 1, de) - Date.UTC(ys, ms - 1, ds)) / 86400000);
}

// A deep link is allowed to be wrong, and one of ours was: MCP handed out
// ...&adults=7 for a house OwnerRez caps at six adults, having just refused a
// quote for that party. OwnerRez enforces three limits (adults, children, and
// the total), so all three are applied here. Landing a guest on the nearest
// bookable party beats landing them on a refusal, and it keeps the selects
// from having to show a number they cannot offer.
function partyFromParams(params, property) {
  const sleeps = property?.sleeps || 16;
  const adults = countFromParams(params, 'adults', {
    fallback: 2,
    min: 1,
    max: Math.min(property?.maxAdults || sleeps, sleeps)
  });
  const children = countFromParams(params, 'children', {
    fallback: 0,
    min: 0,
    max: Math.min(property?.maxChildren ?? sleeps - 1, sleeps - adults)
  });
  return { adults, children };
}

function rangeFromParams(params, property) {
  const empty = { start: null, end: null };
  const start = (params.get('checkin') || '').trim();
  const end = (params.get('checkout') || '').trim();
  if (!YMD.test(start) || !YMD.test(end)) return empty;
  // ISO dates compare correctly as strings, so no Date parsing is needed to
  // reject the past or an inverted range.
  if (end <= start || start < todayYmd()) return empty;
  const nights = nightsBetween(start, end);
  if (nights < (property.minNights || 1)) return empty;
  if (property.maxNights && nights > property.maxNights) return empty;
  return { start, end };
}

function countFromParams(params, key, { fallback, min, max }) {
  const raw = (params.get(key) || '').trim();
  if (!/^\d{1,2}$/.test(raw)) return fallback;
  const n = Number(raw);
  return n >= min && n <= max ? n : fallback;
}

export default function Stay() {
  const { slug } = useParams();
  const property = getProperty(slug);
  const [params] = useSearchParams();
  const wasCanceled = params.get('canceled') === '1';

  // Read once, at mount: after that the calendar and the guest steppers own
  // this state, and re-reading the URL would fight the guest's clicks.
  const [range, setRange] = useState(() => (property ? rangeFromParams(params, property) : { start: null, end: null }));
  // Clamped to the property's real caps, not to `sleeps`. A deep link is
  // allowed to be wrong: MCP happily handed out ...&adults=7 for a house that
  // takes six, and an out-of-range value would leave the select showing a
  // number it cannot offer. Landing them at the nearest bookable party is
  // better than landing them on a refusal.
  const [adults, setAdults] = useState(() => partyFromParams(params, property).adults);
  const [children, setChildren] = useState(() => partyFromParams(params, property).children);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  // Server-computed estimate of the same stay on Airbnb (see
  // src/lib/direct-savings.js); rendered on the quote card next to the total.
  const [airbnbComparison, setAirbnbComparison] = useState(null);

  // Friends-and-family discount entry is an easter egg — no visible
  // affordance, so ordinary guests never ask about it. Three ways in:
  //   1. arrive via a shared link with ?code=XYZ (auto-applies),
  //   2. type "friends" anywhere on the page (desktop),
  //   3. tap the property name five times (phones).
  const [discountCode, setDiscountCode] = useState(() => (params.get('code') || '').trim().toUpperCase());
  const [codeFieldVisible, setCodeFieldVisible] = useState(() => Boolean(params.get('code')));
  const [discountInfo, setDiscountInfo] = useState(null);
  const titleTaps = useRef({ count: 0, last: 0 });

  useEffect(() => {
    let buffer = '';
    function onKeyDown(e) {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      buffer = (buffer + e.key.toLowerCase()).slice(-7);
      if (buffer === 'friends') setCodeFieldVisible(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function onTitleTap() {
    const now = Date.now();
    const taps = titleTaps.current;
    taps.count = now - taps.last < 1500 ? taps.count + 1 : 1;
    taps.last = now;
    if (taps.count >= 5) setCodeFieldVisible(true);
  }

  // Funnel step 1: a complete date range. Keyed on the range alone so a
  // guest-count change (which refetches the quote) does not count twice.
  useEffect(() => {
    if (!range.start || !range.end) return;
    track('dates_selected', { nights: nightsBetween(range.start, range.end), slug: property?.slug });
  }, [range.start, range.end, property?.slug]);

  // Every fetch takes a ticket and a response lands only if it is still the
  // latest: in-flight requests were never cancelled, so rapid guest-count taps
  // could let an earlier response arrive last and overwrite the right quote.
  // Clearing the range also clears the error and the loading flag, so a failed
  // quote no longer lingers under an empty card until the next fetch starts.
  const quoteTicket = useRef(0);
  useEffect(() => {
    const ticket = ++quoteTicket.current;
    if (!range.start || !range.end) {
      setQuote(null);
      setDiscountInfo(null);
      setAirbnbComparison(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    const nights = nightsBetween(range.start, range.end);
    fetchQuote({
      arrival: range.start,
      departure: range.end,
      adults,
      children,
      discountCode: discountCode || undefined
    })
      .then((res) => {
        if (ticket !== quoteTicket.current) return;
        setQuote(res.quote);
        setDiscountInfo(res.discount || null);
        setAirbnbComparison(res.airbnbComparison || null);
        track('quote_returned', { nights, slug: property?.slug });
      })
      .catch((err) => {
        if (ticket !== quoteTicket.current) return;
        setQuoteError(err.message);
        track('quote_failed', { nights, reason: err.message, slug: property?.slug });
      })
      .finally(() => {
        if (ticket === quoteTicket.current) setQuoteLoading(false);
      });
  }, [range.start, range.end, adults, children, discountCode]);

  if (!property) {
    return (
      <NotFound
        title="Property not found."
        seoTitle="Property not found · Charleston.Vacations"
      >
        <Link to="/" className="text-navy-700 underline">Back to today's brief</Link>
      </NotFound>
    );
  }

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title={property.seoTitle || `${property.name} · Book Direct · Charleston.Vacations`}
        description={
          property.seoDescription ||
          `Book our ${property.bedrooms} bed, ${property.bathrooms} bath home in ${property.location} direct. Same calendar as Airbnb and Vrbo, ${DIRECT_DISCOUNT_PCT}% off nightly rates, no service fees. Sleeps ${property.sleeps}.`
        }
        jsonLd={[vacationRentalJsonLd(property, canonicalOrigin()), faqPageJsonLd(property)]}
        image={propertyPhotos(property)[0]?.src}
        imageAlt={propertyPhotos(property)[0]?.alt}
      />
      <Header />
      {/* Masthead-grade hero (owner call, 2026-07-28): centered, kicker in
          gold small caps carrying the search keyword, the name huge in the
          display serif, a concrete deck, and a facts band between hairlines.
          Deliberately NO price here — leading with the cheapest night anchors
          the page at budget level; rates live in the seasonal-range FAQ and
          the quote. Title, then the house, then the calendar: the gallery
          answers "what am I booking" before the booking module loads. */}
      {/* mt-6 and a text-base deck on phones: under the compact interior
          masthead they bring the house onto the first screen. */}
      <header className="mt-6 text-center">
        <h1 onClick={onTitleTap}>
          <span className="block text-[11px] font-medium uppercase tracking-[0.22em] text-cream-600">
            {property.heroKicker || `${property.location} vacation rental`}
          </span>
          {/* Two block spans put nothing between them in textContent, so the
              h1 read and scraped as "A Mount Pleasant Vacation
              RentalSaltgrass Cottage". A screen-reader-only separator is in
              the text without being on the page. */}
          <span className="sr-only">: </span>
          <span className="mt-2 block font-display text-4xl font-semibold text-navy-900 sm:text-5xl">
            {property.name}
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-read font-serif text-base leading-relaxed text-navy-800 sm:text-lg">
          {property.heroDeck || property.headline}
        </p>
        {property.heroDirectLine && (
          <p className="mx-auto mt-2 max-w-read font-serif text-[15px] italic text-navy-600">
            {property.heroDirectLine}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 border-y border-navy-100 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-navy-700">
          <span>{property.bedrooms} bedrooms</span>
          <span className="text-cream-500" aria-hidden="true">·</span>
          <span>{property.bathrooms} baths</span>
          <span className="text-cream-500" aria-hidden="true">·</span>
          <span>Sleeps {property.sleeps}</span>
          {property.reviews?.rating && (
            <>
              <span className="text-cream-500" aria-hidden="true">·</span>
              <span className="text-navy-900">
                {property.reviews.rating}★ across {property.reviews.count} reviews
              </span>
            </>
          )}
        </div>
      </header>

      <Gallery photos={propertyPhotos(property)} propertyName={property.name} />

      <div className="mt-8 border-b border-navy-100" />

      {wasCanceled && (
        <div className="mt-4 rounded-lg border border-cream-300 bg-cream-50 p-3 text-sm text-navy-700">
          Booking canceled. Pick new dates to try again.
        </div>
      )}

      {/* The calendar owns the full content width (owner call, 2026-07-28):
          date-picking is the commitment moment, so it gets the room. The
          quote + book rail follows in the split below and sticks while the
          guest reads on. */}
      <section className="mt-6">
        <Eyebrow text="Plan your stay" />
        <h2 className="mb-4 font-display text-2xl font-semibold text-navy-900">Availability</h2>
        <AvailabilityCalendar
          onRangeChange={setRange}
          minNights={property.minNights || 1}
          initialRange={range}
        />
      </section>

      {/* The rail is a fixed 288px, not a fraction: at 2fr_1fr inside the
          672px measure it came out 213px and the ledger labels wrapped. */}
      <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_288px]">
        <section>
          <Eyebrow text="The short version" />
          <h2 className="mb-4 font-display text-2xl font-semibold text-navy-900">About the stay</h2>
          <div className="prose prose-navy max-w-none text-navy-800">
            <p>{property.description}</p>
          </div>
        </section>

        {/* order-first: on a phone the priced card lands right under the calendar,
            so the check-out tap shows a price, not the description paragraph.
            lg:order-none restores the desktop split. */}
        <aside className="order-first space-y-4 lg:sticky lg:top-6 lg:order-none lg:self-start">
          <QuoteSummary quote={quote} loading={quoteLoading} error={quoteError} comparison={airbnbComparison} />
          <BookingForm
            property={property}
            range={range}
            quote={quote}
            quoteError={quoteError}
            adults={adults}
            childrenCount={children}
            onAdultsChange={setAdults}
            onChildrenChange={setChildren}
            showDiscountField={codeFieldVisible}
            discountCode={discountCode}
            discountInfo={discountInfo}
            onApplyDiscountCode={setDiscountCode}
          />
        </aside>
      </div>

      {/* Objection-handling stack: everything a guest checks before trusting a
          direct site, in the order they tend to ask. The daily-brief card lives
          down here too — next to the title it read as a review score. */}
      <div className="mt-10">
        <Section eyebrow="Booked-direct perk" title="A local brief, every morning of your stay">
          <p className="mb-4 text-sm leading-relaxed text-navy-700">
            The daily Charleston brief on this site is written for guests of this home.
            Today's read:
          </p>
          <BriefSnapshot />
        </Section>
        <HomeDetails property={property} />
        <HostBlock property={property} />
        <Reviews property={property} />
        <Neighborhood property={property} />
        <CancellationPolicy property={property} />
        <Faq property={property} />
      </div>

      <Footer />
    </div>
  );
}
